import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export type Mode = 'agriculture' | 'household';

interface ModeContextProps {
  mode: Mode;
  setMode: (mode: Mode) => void;
  toggleMode: () => void;
  isAgriculture: boolean;
  isHousehold: boolean;
}

const ModeContext = createContext<ModeContextProps | undefined>(undefined);

export const ModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<Mode>('household');
  const queryClient = useQueryClient();

  useEffect(() => {
    // Get saved mode preference from localStorage
    const savedMode = localStorage.getItem('aquagenius-mode') as Mode;
    if (savedMode) {
      setModeState(savedMode);
    }
  }, []);

  useEffect(() => {
    // Save mode preference
    localStorage.setItem('aquagenius-mode', mode);
    
    // Apply mode-specific CSS classes
    document.documentElement.classList.remove('mode-agriculture', 'mode-household');
    document.documentElement.classList.add(`mode-${mode}`);

    // Dispatch a custom event for components that need to react to mode changes
    window.dispatchEvent(new CustomEvent('modechange', { detail: { mode } }));

    // Invalidate and refetch relevant queries
    queryClient.invalidateQueries({ queryKey: ['waterUsage'] });
    queryClient.invalidateQueries({ queryKey: ['insights'] });
    queryClient.invalidateQueries({ queryKey: ['leaderboard'] });
  }, [mode, queryClient]);

  const setMode = (newMode: Mode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    setModeState(prevMode => prevMode === 'household' ? 'agriculture' : 'household');
  };

  const value = {
    mode,
    setMode,
    toggleMode,
    isAgriculture: mode === 'agriculture',
    isHousehold: mode === 'household'
  };

  return (
    <ModeContext.Provider value={value}>
      {children}
    </ModeContext.Provider>
  );
};

export const useMode = () => {
  const context = useContext(ModeContext);
  if (context === undefined) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
};

// CSS variables for mode-specific themes
const modeStyles = `
  :root {
    /* Common variables */
    --primary-light: #0EA5E9;
    --primary-dark: #0369A1;
    
    /* Household mode variables */
    --mode-household-bg: #E0F2FE;
    --mode-household-text: #0C4A6E;
    --mode-household-accent: #0EA5E9;
    --mode-household-surface: #FFFFFF;
    
    /* Agriculture mode variables */
    --mode-agriculture-bg: #DCFCE7;
    --mode-agriculture-text: #166534;
    --mode-agriculture-accent: #16A34A;
    --mode-agriculture-surface: #FFFFFF;
  }

  .mode-household {
    --app-bg: var(--mode-household-bg);
    --app-text: var(--mode-household-text);
    --app-accent: var(--mode-household-accent);
    --app-surface: var(--mode-household-surface);
  }

  .mode-agriculture {
    --app-bg: var(--mode-agriculture-bg);
    --app-text: var(--mode-agriculture-text);
    --app-accent: var(--mode-agriculture-accent);
    --app-surface: var(--mode-agriculture-surface);
  }

  /* Dark mode overrides */
  .dark .mode-household {
    --app-bg: #082F49;
    --app-text: #F0F9FF;
    --app-accent: #38BDF8;
    --app-surface: #0C4A6E;
    --app-shadow: rgba(56, 189, 248, 0.1);
  }

  .dark .mode-agriculture {
    --app-bg: #052E16;
    --app-text: #F0FDF4;
    --app-accent: #4ADE80;
    --app-surface: #166534;
    --app-shadow: rgba(74, 222, 128, 0.1);
  }
`;

// Inject mode styles
const styleSheet = document.createElement('style');
styleSheet.textContent = modeStyles;
document.head.appendChild(styleSheet);